import {
  ArrowUpToLine,
  ChevronDown,
  Home,
  LogOut,
  Menu as MenuIcon,
  MonitorPlay,
  Search,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';

import { usePerfil } from '@/features/cuenta/queries';

import { menuPrincipal, menuUsuario, type ItemMenu, type Menu } from '@/app/navegacion';
import { useAuth } from '@/auth/AuthProvider';
import { useConstancias, useEntitlements, useMe } from '@/auth/queries';
import { usePageEnter } from '@/lib/motion';
import { iniciales } from '@/lib/utils';

import { AvisoBeta } from './AvisoBeta';
import { AvisoEnarm } from './AvisoEnarm';
import { InsigniaEntorno } from './InsigniaEntorno';
import { SelectorTema } from './SelectorTema';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

function useTiene() {
  const { data } = useEntitlements();
  return (permiso?: string) => !permiso || !data || data.permissions.includes(permiso);
}

/** Menú de usuario; "Cartas y Constancias" solo aparece a quien compró un curso. */
function useMenuUsuario(): ItemMenu[] {
  const { data } = useConstancias();
  return menuUsuario.filter((i) => i.ruta !== '/constancias' || data?.eligible === true);
}

function ItemDropdown({ item }: { item: ItemMenu }) {
  const navigate = useNavigate();
  const tiene = useTiene()(item.permiso);
  return (
    <DropdownMenuItem
      onSelect={() => void navigate(item.ruta)}
      className={tiene ? '' : 'opacity-60'}
    >
      <span className="flex-1">{item.etiqueta}</span>
      {!tiene && <span className="insignia insignia-exclusivo">Exclusivo</span>}
    </DropdownMenuItem>
  );
}

function MenuEscritorio({ menu }: { menu: Menu }) {
  const tiene = useTiene()(menu.permiso);
  if (!menu.items) {
    return (
      <NavLink
        to={menu.ruta ?? '/'}
        className={({ isActive }) =>
          `rounded-lg px-3 py-2 text-sm font-bold text-white hover:bg-white/15 ${isActive ? 'bg-white/15' : ''}`
        }
      >
        {menu.etiqueta}
        {!tiene && <span className="insignia insignia-exclusivo ml-2">Exclusivo</span>}
      </NavLink>
    );
  }
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-bold text-white outline-none hover:bg-white/15 data-[state=open]:bg-white/15">
        {menu.etiqueta} <ChevronDown size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start">
        {menu.items.map((i) => (
          <ItemDropdown key={i.ruta + i.etiqueta} item={i} />
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function MenuUsuario() {
  const { data: me } = useMe();
  const items = useMenuUsuario();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const nombre = me?.nickname ?? '';
  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger className="flex items-center gap-2 rounded-full py-1 pr-2 pl-1 text-white outline-none hover:bg-white/15 data-[state=open]:bg-white/15">
        <span className="grid h-9 w-9 place-items-center rounded-full bg-white/20 text-sm font-extrabold">
          {iniciales(me?.name ?? '·')}
        </span>
        <span className="hidden max-w-36 truncate text-sm font-bold xl:inline">{nombre}</span>
        <ChevronDown size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>{me?.email}</DropdownMenuLabel>
        {items.map((i) => (
          <ItemDropdown key={i.ruta} item={i} />
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={() => {
            void signOut().then(() => navigate('/login', { replace: true }));
          }}
        >
          <LogOut size={16} /> Cerrar sesión
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Buscador({ className = '' }: { className?: string }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  return (
    <form
      role="search"
      className={`flex items-center gap-2 ${className}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim()) void navigate(`/busqueda?q=${encodeURIComponent(q.trim())}`);
      }}
    >
      <label className="relative flex-1">
        <span className="sr-only">Buscar en Consalud</span>
        <Search
          size={16}
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-slate-500"
        />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar en Consalud"
          className="w-full rounded-full bg-white py-2 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-cian-500"
        />
      </label>
    </form>
  );
}

function MenuMovil({ menus, onClose }: { menus: Menu[]; onClose: () => void }) {
  const tiene = useTiene();
  const menuUsuarioVisible = useMenuUsuario();
  const { signOut } = useAuth();
  const navigate = useNavigate();
  return (
    <div className="fixed inset-0 z-50 lg:hidden" role="dialog" aria-modal="true" aria-label="Menú">
      <button
        type="button"
        className="overlay-cs absolute inset-0"
        aria-label="Cerrar menú"
        onClick={onClose}
      />
      <nav className="vidrio-fuerte animate__animated animate__fadeInRight absolute inset-y-0 right-0 flex w-[min(22rem,90vw)] flex-col gap-1 overflow-y-auto p-4 [--animate-duration:300ms]">
        <div className="mb-2 flex items-center justify-between">
          <span className="font-extrabold text-azul-texto">Menú</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 hover:bg-[var(--vidrio-fuerte)]"
            aria-label="Cerrar"
          >
            <X size={20} />
          </button>
        </div>
        <Buscador className="mb-3" />
        {[...menus, { etiqueta: 'Mi cuenta', items: menuUsuarioVisible }].map((m) => (
          <div key={m.etiqueta} className="border-b border-[var(--separador)] py-2">
            {m.items ? (
              <>
                <p className="px-2 pb-1 text-xs font-extrabold tracking-wide text-texto-3 uppercase">
                  {m.etiqueta}
                </p>
                {m.items.map((i) => (
                  <Link
                    key={i.ruta + i.etiqueta}
                    to={i.ruta}
                    onClick={onClose}
                    className={`flex items-center justify-between rounded-lg px-2 py-2 text-sm font-semibold text-texto-2 hover:bg-[var(--vidrio-fuerte)] ${tiene(i.permiso) ? '' : 'opacity-60'}`}
                  >
                    {i.etiqueta}
                    {!tiene(i.permiso) && (
                      <span className="insignia insignia-exclusivo">Exclusivo</span>
                    )}
                  </Link>
                ))}
              </>
            ) : (
              <Link
                to={m.ruta ?? '/'}
                onClick={onClose}
                className="block rounded-lg px-2 py-2 font-bold text-texto hover:bg-[var(--vidrio-fuerte)]"
              >
                {m.etiqueta}
              </Link>
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn-cs btn-peligro mt-4"
          onClick={() => void signOut().then(() => navigate('/login', { replace: true }))}
        >
          <LogOut size={16} /> Cerrar sesión
        </button>
      </nav>
    </div>
  );
}

/** Barra inferior móvil (< lg). El simulador apunta al v2 (corrige mapa-vistas hallazgo 3). */
function BarraInferior() {
  const cls = ({ isActive }: { isActive: boolean }) =>
    `flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-bold ${isActive ? 'text-cian-texto' : 'text-texto-2'}`;
  return (
    <nav
      className="vidrio-fuerte fixed inset-x-0 bottom-0 z-40 flex rounded-t-2xl lg:hidden"
      aria-label="Accesos rápidos"
    >
      <NavLink to="/inicio" className={cls}>
        <Home size={20} /> Inicio
      </NavLink>
      <NavLink to="/simulador" className={cls}>
        <Zap size={20} /> Simulador
      </NavLink>
      <NavLink to="/curso-enarm/clases" className={cls}>
        <MonitorPlay size={20} /> Clases
      </NavLink>
      <button
        type="button"
        className={cls({ isActive: false })}
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
      >
        <ArrowUpToLine size={20} /> Subir
      </button>
    </nav>
  );
}

/** Shell autenticado (skill §1): barra con el degradado de marca, aviso ENARM y barra inferior. */
export function AppShell() {
  const { data: ent } = useEntitlements();
  const anio = ent?.enarmYear ?? new Date().getFullYear();
  const menus = menuPrincipal(anio);
  const [abierto, setAbierto] = useState(false);
  const location = useLocation();
  const main = useRef<HTMLElement>(null);
  usePageEnter(main, [location.pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [location.pathname]);

  // Como el PHP: mientras falten datos del perfil, el teléfono o un correo real, se lleva al
  // alumno a "Mi cuenta" (salvo licencias y pagos).
  const perfil = usePerfil();
  const navigate = useNavigate();
  useEffect(() => {
    const n = perfil.data?.needs;
    const libre = ['/cuenta', '/licencias', '/pagar'].some((p) => location.pathname.startsWith(p));
    if (n && (n.profile || n.phone || n.realEmail) && !libre)
      void navigate('/cuenta', { replace: true });
  }, [perfil.data, location.pathname, navigate]);

  return (
    <>
      <a
        href="#contenido"
        className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 focus:rounded-lg focus:bg-white focus:px-3 focus:py-2 focus:text-slate-900"
      >
        Saltar al contenido
      </a>
      <header className="bg-marca shadow-barra fixed inset-x-0 top-0 z-40 opacity-[.97] backdrop-blur-xl">
        <nav
          className="mx-auto flex h-16 max-w-screen-2xl items-center gap-3 px-4"
          aria-label="Principal"
        >
          <Link to="/inicio" className="shrink-0" aria-label="Consalud ENARM, inicio">
            <img
              src="/img/consalud-horizontal-blanco.png"
              alt="Consalud ENARM"
              className="h-9 w-auto"
            />
          </Link>
          <div className="hidden flex-1 items-center gap-0.5 lg:flex">
            {menus.map((m) => (
              <MenuEscritorio key={m.etiqueta} menu={m} />
            ))}
          </div>
          <Buscador className="hidden w-56 2xl:flex" />
          <div className="ml-auto flex items-center gap-2 lg:ml-0">
            <SelectorTema />
            <div className="hidden lg:block">
              <MenuUsuario />
            </div>
            <button
              type="button"
              className="rounded-full p-2 text-white hover:bg-white/15 lg:hidden"
              onClick={() => setAbierto(true)}
              aria-label="Abrir menú"
            >
              <MenuIcon size={24} />
            </button>
          </div>
        </nav>
      </header>
      <div className="pt-16">
        <AvisoBeta />
        <AvisoEnarm />
      </div>
      <main id="contenido" ref={main} className="mx-auto max-w-screen-xl px-4 pt-6 pb-28 lg:pb-12">
        <Outlet />
      </main>
      <BarraInferior />
      <InsigniaEntorno />
      {abierto && <MenuMovil menus={menus} onClose={() => setAbierto(false)} />}
    </>
  );
}
